import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { DataGrid } from '../../components/DataGrid.js';

interface ProviderRow {
  readonly id: string;
  readonly channel: string;
  readonly name: string;
  readonly isActive: boolean;
  readonly failureCount: number;
  readonly priority: number;
}

export function NotificationsProvidersPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ProviderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [providerCode, setProviderCode] = useState('');
  const [providerName, setProviderName] = useState('');
  const [channel, setChannel] = useState('email');
  const [configuration, setConfiguration] = useState('{}');
  const [priority, setPriority] = useState('1');
  const [dailyLimit, setDailyLimit] = useState('');
  const [isFailover, setIsFailover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = (): void => {
    apiRequest<{ items: ProviderRow[] }>('/notifications/providers', { token })
      .then((r) => setRows(r.items))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const resetForm = (): void => {
    setEditingId(null);
    setProviderCode('');
    setProviderName('');
    setChannel('email');
    setConfiguration('{}');
    setPriority('1');
    setDailyLimit('');
    setIsFailover(false);
  };

  const startEdit = (r: ProviderRow): void => {
    setEditingId(r.id);
    setProviderCode(r.id);
    setProviderName(r.name);
    setChannel(r.channel);
    setConfiguration('{}');
    setPriority(String(r.priority));
    setDailyLimit('');
    setIsFailover(false);
    setSaved(false);
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    let parsedConfig: Record<string, unknown>;
    try {
      parsedConfig = configuration.trim() ? JSON.parse(configuration) : {};
    } catch {
      setError(t('notif.providers.invalidJson'));
      setSaving(false);
      return;
    }
    try {
      const payload = {
        providerCode,
        providerName,
        channel,
        configuration: parsedConfig,
        priority: priority ? Number(priority) : undefined,
        dailyLimit: dailyLimit ? Number(dailyLimit) : undefined,
        isFailover,
      };
      if (editingId) {
        await apiRequest(`/notifications/providers/${editingId}`, { method: 'PATCH', token, body: payload });
      } else {
        await apiRequest('/notifications/providers', { method: 'POST', token, body: payload });
      }
      setSaved(true);
      resetForm();
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const resetFailures = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/notifications/providers/${id}/reset-failures`, { token, method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="provider-configuration">
      <h1>{t('notif.providers.title')}</h1>

      <form onSubmit={onSubmit} aria-label="provider-form">
        <h2>{editingId ? t('notif.providers.editProvider') : t('notif.providers.addProvider')}</h2>
        <label htmlFor="pv-code">{t('notif.providers.providerCode')}</label>
        <input id="pv-code" value={providerCode} onChange={(e) => setProviderCode(e.target.value)} required />
        <label htmlFor="pv-name">{t('notif.providers.providerName')}</label>
        <input id="pv-name" value={providerName} onChange={(e) => setProviderName(e.target.value)} required />
        <label htmlFor="pv-channel">{t('notif.common.channel')}</label>
        <select id="pv-channel" aria-label="provider-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="email">{t('notif.common.chEmail')}</option>
          <option value="sms">{t('notif.common.chSms')}</option>
          <option value="push">{t('notif.common.chPush')}</option>
        </select>
        <label htmlFor="pv-config">{t('notif.providers.configJson')}</label>
        <textarea id="pv-config" value={configuration} onChange={(e) => setConfiguration(e.target.value)} />
        <label htmlFor="pv-priority">{t('notif.common.priority')}</label>
        <input id="pv-priority" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
        <label htmlFor="pv-limit">{t('notif.providers.dailyLimit')}</label>
        <input id="pv-limit" type="number" value={dailyLimit} onChange={(e) => setDailyLimit(e.target.value)} />
        <label htmlFor="pv-failover">
          <input id="pv-failover" type="checkbox" checked={isFailover} onChange={(e) => setIsFailover(e.target.checked)} />
          {t('notif.providers.failover')}
        </label>
        {error ? <p role="alert">{error}</p> : null}
        {saved ? <p role="status">{t('notif.providers.saved')}</p> : null}
        <Button type="submit" disabled={saving} variant="primary">{saving ? t('notif.providers.saving') : editingId ? t('notif.providers.updateProvider') : t('notif.providers.addProvider')}</Button>
        {editingId ? <Button type="button" onClick={resetForm} variant="danger">{t('notif.common.cancel')}</Button> : null}
      </form>

      <DataGrid<any>
        caption="providers-table"
        rows={rows}
        getRowId={(r) => r.id}
        columns={[
          { key: 'channel', header: t('notif.common.channel'), value: (r) => r.channel },
          { key: 'provider', header: t('notif.common.provider'), value: (r) => r.name },
          { key: 'priority', header: t('notif.common.priority'), value: (r) => r.priority, numeric: true },
          { key: 'failures', header: t('notif.providers.failures'), value: (r) => r.failureCount, numeric: true },
          { key: 'state', header: t('notif.providers.state'), value: (r) => (r.isActive ? t('notif.providers.active') : t('notif.providers.disabled')) },
        ]}
        actionsHeader={t('notif.common.action')}
        actions={(r) => (
          <>
            <Button type="button" aria-label={`edit-${r.id}`} onClick={() => startEdit(r)} variant="secondary">{t('notif.providers.edit')}</Button>
            {r.failureCount > 0 && (
              <Button type="button" disabled={busyId === r.id} onClick={() => void resetFailures(r.id)} variant="secondary">
                {busyId === r.id ? t('notif.providers.resetting') : t('notif.providers.resetFailures')}
              </Button>
            )}
          </>
        )}
      />
    </main>
  );
}
